import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, PenLine, Loader2, Send, X } from "lucide-react";
import { Reveal } from "./Reveal";
import * as Dialog from "@radix-ui/react-dialog";
import { toast } from "sonner";
import { API_BASE } from "../lib/api";

const API = API_BASE;

function initials(name) {
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("");
}

function Stars({ value, size = 14 }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= value ? "text-[#d4a840] fill-[#d4a840]" : "text-white/20"}
        />
      ))}
    </div>
  );
}

function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" data-testid="review-rating-picker">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          data-testid={`review-rating-star-${i}`}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(i)}
          className="p-0.5 transition-transform hover:scale-110"
          aria-label={`Rate ${i} star${i > 1 ? "s" : ""}`}
        >
          <Star
            size={26}
            className={(hover || value) >= i ? "text-[#d4a840] fill-[#d4a840]" : "text-white/25"}
          />
        </button>
      ))}
    </div>
  );
}

const initialForm = { name: "", company: "", message: "", rating: 5 };

export function TestimonialsSection() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/testimonials`)
      .then((res) => {
        if (!cancelled && Array.isArray(res.data)) {
          setTestimonials(res.data);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/testimonials`, form);
      setTestimonials((prev) => [data, ...prev]);
      toast.success("Thanks for the review — it's now live on our site.");
      setForm(initialForm);
      setOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your inputs and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="relative py-24 lg:py-32 max-w-7xl mx-auto px-6 lg:px-10" data-testid="testimonials-section">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <div className="kb-label mb-4">Client Voices</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-2xl">
              Trusted by founders <span className="italic text-[#d4a840]">who value certainty.</span>
            </h2>
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="kb-btn-ghost shrink-0"
            data-testid="write-review-trigger"
          >
            Write a review <PenLine size={16} />
          </button>
        </div>
      </Reveal>

      {!loading && testimonials.length === 0 ? (
        <Reveal>
          <div className="kb-glass rounded-2xl py-16 px-6 flex flex-col items-center text-center" data-testid="testimonials-empty">
            <div className="h-14 w-14 rounded-2xl bg-[#d4a840]/10 border border-[#d4a840]/30 flex items-center justify-center mb-6">
              <Quote size={24} className="text-[#d4a840]" />
            </div>
            <h3 className="font-display text-2xl font-semibold text-white">No reviews yet</h3>
            <p className="mt-3 text-white/60 max-w-md leading-relaxed">
              Be the first to share how KaveriBiz helped your business — your review will appear right here.
            </p>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="kb-btn-primary mt-8"
              data-testid="write-review-trigger-empty"
            >
              Write the first review <PenLine size={16} />
            </button>
          </div>
        </Reveal>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {testimonials.slice(0, 6).map((t, i) => (
            <Reveal key={t.id} delay={i * 0.05}>
              <div className="group h-full kb-glass rounded-2xl p-7 flex flex-col" data-testid={`testimonial-card-${t.id}`}>
                <div className="flex items-start justify-between mb-6">
                  <div className="h-11 w-11 rounded-xl bg-[#d4a840]/10 border border-[#d4a840]/30 flex items-center justify-center">
                    <Quote size={18} className="text-[#d4a840]" />
                  </div>
                  <Stars value={t.rating} />
                </div>
                <p className="text-sm text-white/75 leading-relaxed flex-1">"{t.message}"</p>
                <div className="mt-6 pt-5 border-t border-[#d4a840]/15 flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-[#d4a840]/15 border border-[#d4a840]/40 flex items-center justify-center text-xs font-semibold text-[#d4a840] shrink-0">
                    {initials(t.name)}
                  </div>
                  <div className="leading-tight min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{t.name}</div>
                    {t.company ? <div className="text-xs text-white/50 truncate">{t.company}</div> : null}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      )}

      <Dialog.Root open={open} onOpenChange={setOpen}>
        <AnimatePresence>
          {open && (
            <Dialog.Portal forceMount>
              <Dialog.Overlay asChild forceMount>
                <motion.div
                  className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                />
              </Dialog.Overlay>
              <Dialog.Content asChild forceMount aria-describedby="review-dialog-description">
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
                <motion.div
                  className="w-full max-w-lg my-auto kb-glass rounded-2xl bg-[#0a1330] p-7 sm:p-8 shadow-2xl"
                  initial={{ opacity: 0, y: 16, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.98 }}
                  transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  data-testid="review-dialog"
                >
                  <Dialog.Close
                    className="absolute right-5 top-5 text-white/50 hover:text-[#d4a840] transition-colors"
                    data-testid="review-dialog-close"
                  >
                    <X size={18} />
                    <span className="sr-only">Close</span>
                  </Dialog.Close>

                  <div className="kb-label mb-3">Share your experience</div>
                  <Dialog.Title className="font-display text-2xl md:text-3xl font-bold text-white leading-snug">
                    Leave a review
                  </Dialog.Title>
                  <Dialog.Description id="review-dialog-description" className="mt-2 text-sm text-white/60 leading-relaxed">
                    Tell other businesses about working with KaveriBiz. Your review is published right away.
                  </Dialog.Description>

                  <form onSubmit={onSubmit} className="mt-7 space-y-5" data-testid="review-form">
                    <div>
                      <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-2">Your rating</label>
                      <StarPicker value={form.rating} onChange={(rating) => setForm({ ...form, rating })} />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-2">Full name</label>
                        <input
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={onChange}
                          required
                          minLength={2}
                          data-testid="review-field-name"
                          className="w-full bg-[#0a1f4d]/60 border border-[#d4a840]/20 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#d4a840] focus:outline-none transition-colors"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-2">Company (optional)</label>
                        <input
                          type="text"
                          name="company"
                          value={form.company}
                          onChange={onChange}
                          data-testid="review-field-company"
                          className="w-full bg-[#0a1f4d]/60 border border-[#d4a840]/20 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#d4a840] focus:outline-none transition-colors"
                          placeholder="Business name"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-2">Your review</label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={onChange}
                        required
                        minLength={10}
                        rows={4}
                        data-testid="review-field-message"
                        className="w-full bg-[#0a1f4d]/60 border border-[#d4a840]/20 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#d4a840] focus:outline-none transition-colors resize-none"
                        placeholder="What was it like working with us?"
                      />
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-1">
                      <Dialog.Close asChild>
                        <button type="button" className="text-sm text-white/50 hover:text-white transition-colors px-2">
                          Cancel
                        </button>
                      </Dialog.Close>
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="review-submit"
                        className="kb-btn-primary disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {submitting ? <><Loader2 size={18} className="animate-spin" /> Posting...</> : <>Post review <Send size={16} /></>}
                      </button>
                    </div>
                  </form>
                </motion.div>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          )}
        </AnimatePresence>
      </Dialog.Root>
    </section>
  );
}
